import { FC, useState } from 'react';
import { graphql, useFragment, useRelayEnvironment } from 'react-relay';
import cn from 'classnames';
import dibsCss from 'dibs-css';
import { FormattedMessage } from 'dibs-react-intl';
import PayPalIcon from 'dibs-icons/exports/legacy/PaypalLogo';
import { Link } from 'dibs-elements/exports/Link';
import { PaymentMethodSelectOption } from '../PaymentMethodSelectOption';
import { usePaymentMethodSelectContext } from '../PaymentMethodSelectContext';
import { submitPayPalPaymentMethod } from './submitPayPalPaymentMethod';
import { useDeleteWalletPaymentMethodMutation } from '../wallet/deleteWalletPaymentMethod';
import { type PaymentMethod } from '../../types';
import { usePayPalClient } from './usePayPalClient';

import { PayPalPaymentMethod_buyer$key } from './__generated__/PayPalPaymentMethod_buyer.graphql';
import { PayPalPaymentMethod_viewer$key } from './__generated__/PayPalPaymentMethod_viewer.graphql';

const viewerFragment = graphql`
    fragment PayPalPaymentMethod_viewer on Viewer {
        id
        clientToken {
            value
        }
    }
`;

const buyerFragment = graphql`
    fragment PayPalPaymentMethod_buyer on User {
        serviceId
        wallet {
            paymentMethods {
                shippingAddresses {
                    serviceId
                }
                paymentMethod {
                    paymentMethodType
                    ... on PayPalAccount {
                        token
                        email
                    }
                }
            }
        }
    }
`;

type Props = {
    viewer: PayPalPaymentMethod_viewer$key | null;
    buyer: PayPalPaymentMethod_buyer$key | null;
};

export const PayPalPaymentMethod: FC<Props> = props => {
    const { viewer: viewerRef, buyer: buyerRef } = props;

    const [isRemoving, setIsRemoving] = useState(false);

    const relayEnvironment = useRelayEnvironment();
    const viewer = useFragment(viewerFragment, viewerRef);
    const buyer = useFragment(buyerFragment, buyerRef);

    const {
        userId,
        selectedPaymentMethod,
        updateSelectedPaymentMethod,
        isBusy,
        setIsBusy,
        paymentMethodOptions,
        mockWallet,
        shippingAddressId,
    } = usePaymentMethodSelectContext();

    const [commitDeleteMutation] = useDeleteWalletPaymentMethodMutation();

    const isSelected =
        selectedPaymentMethod?.paymentMethodType === 'PAYPAL_ACCOUNT' &&
        selectedPaymentMethod?.paymentProcessor === 'BRAINTREE';

    const walletPaymentMethod = buyer?.wallet?.paymentMethods?.find(
        method => method?.paymentMethod?.paymentMethodType === 'PAYPAL_ACCOUNT'
    );
    const { email, token } = walletPaymentMethod?.paymentMethod || {};

    const payPalClient = usePayPalClient(viewer?.clientToken?.value || '');

    const isValidForShippingAddress =
        !shippingAddressId ||
        walletPaymentMethod?.shippingAddresses?.some(
            address => address?.serviceId === shippingAddressId
        ) ||
        false;

    function handleChange(): void {
        const paymentMethodToken = token || undefined;
        updateSelectedPaymentMethod({
            paymentMethodType: 'PAYPAL_ACCOUNT',
            paymentProcessor: 'BRAINTREE',
            paymentMethodToken: isValidForShippingAddress ? paymentMethodToken : undefined,
        });
    }

    async function handleRemoveClick(): Promise<void> {
        if (!userId || !email) {
            return;
        }

        setIsBusy(true);
        setIsRemoving(true);
        commitDeleteMutation({
            variables: {
                input: {
                    userId,
                    paymentMethodKey: email,
                    paymentMethodType: 'PAYPAL_ACCOUNT',
                },
            },
            onCompleted() {
                setIsRemoving(false);
                setIsBusy(false);
                updateSelectedPaymentMethod({
                    paymentMethodType: 'PAYPAL_ACCOUNT',
                    paymentProcessor: 'BRAINTREE',
                    paymentMethodToken: undefined,
                });
            },
            onError() {
                setIsRemoving(false);
                setIsBusy(false);
            },
        });
    }

    async function handleLogInClick(): Promise<void> {
        if (!userId || isBusy) {
            return;
        }

        try {
            setIsBusy(true);
            const savedToken = await submitPayPalPaymentMethod(payPalClient, {
                userId,
                viewerId: viewer?.id || '',
                verifyPaymentMethod: !!paymentMethodOptions?.PAYPAL_ACCOUNT?.verifyPaymentMethod,
                relayEnvironment,
                mockWallet,
            });
            if (savedToken) {
                const paymentMethod: PaymentMethod = {
                    paymentMethodType: 'PAYPAL_ACCOUNT',
                    paymentProcessor: 'BRAINTREE',
                    paymentMethodToken: savedToken,
                };
                updateSelectedPaymentMethod(paymentMethod);
                // calling onVault callback to replicate behavior from checkout, where vaulting paypal submits payment step
                paymentMethodOptions?.PAYPAL_ACCOUNT?.onVault?.(paymentMethod);
            }
            setIsBusy(false);
        } catch (e: $TSFixMe) {
            if (e?.error?.type === 'VAULT') {
                paymentMethodOptions?.PAYPAL_ACCOUNT?.onVaultError?.(e?.error?.payPalError);
            } else {
                paymentMethodOptions?.PAYPAL_ACCOUNT?.onSetupError?.(e?.error?.payPalError);
            }
            setIsBusy(false);
        }
    }

    const isLoggedInToPayPal = !!token && isValidForShippingAddress;

    return (
        <PaymentMethodSelectOption
            isChecked={isSelected}
            onChange={handleChange}
            dataTn="payment-method-payPalAccount-input"
            name="payment-method"
            value="PAYPAL_ACCOUNT-BRAINTREE"
            icon={<PayPalIcon className={dibsCss.block} />}
            label={
                <div className={cn(dibsCss.flexGrow, dibsCss.sassyFontBodyMediumHeavy)}>
                    <FormattedMessage
                        id="dibs-payment.PayPalPaymentMethod.title"
                        defaultMessage="PayPal"
                    />
                </div>
            }
            content={
                isSelected ? (
                    <>
                        <p
                            className={cn(dibsCss.mtSmall, dibsCss.mbSmall)}
                            data-tn="billing-form-help-text"
                        >
                            {isLoggedInToPayPal ? (
                                <FormattedMessage
                                    id="dibs-payment.PayPalPaymentMethod.selectedHelp"
                                    defaultMessage="You have selected PayPal as your payment method."
                                />
                            ) : (
                                <FormattedMessage
                                    id="dibs-payment.PayPalPaymentMethod.notSelectedHelp"
                                    defaultMessage="Click the button below to authorize payment through your PayPal account. You will have a chance to review all of your details before submitting your order."
                                />
                            )}
                        </p>
                        {isLoggedInToPayPal ? (
                            <div>
                                <strong className="fs-block">{email}</strong>
                                {!isRemoving && (
                                    <Link
                                        className={cn(
                                            dibsCss.sassyFontSubHeaderSizeSmallTypeNormal,
                                            dibsCss.mlXsmall
                                        )}
                                        onClick={handleRemoveClick}
                                    >
                                        <span
                                            className={cn(
                                                dibsCss.inlineBlock,
                                                dibsCss.mlXsmall,
                                                dibsCss.mrXsmall
                                            )}
                                            data-tn="paypal-checkout-remove-icon"
                                        >
                                            ✕
                                        </span>
                                        <FormattedMessage
                                            id="dibs-payment.PayPalPaymentMethod.removeLink"
                                            defaultMessage="Remove"
                                        />
                                    </Link>
                                )}
                            </div>
                        ) : (
                            payPalClient && (
                                <Link onClick={handleLogInClick}>
                                    <img
                                        className={dibsCss.w100Px}
                                        src="https://a.1stdibscdn.com/dist/adhoc/app-buyer-commerce/pay-with-paypal.png"
                                        alt="PayPal"
                                        data-tn="login-paypal-button"
                                    />
                                </Link>
                            )
                        )}
                    </>
                ) : null
            }
        />
    );
};
